import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { StatusBadge } from '@/components/common/StatusBadge';
import DocumentsSection from '@/components/common/DocumentsSection';
import { ActivityLog } from '@/components/ActivityLog';
import PaymentRibbon, { computePaymentRibbon } from '@/components/projects/PaymentRibbon';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  FolderKanban, ArrowLeft, MapPin, User, FileText,
  Wrench, Shield, Receipt, Pencil, Navigation, Landmark,
  Plus, Eye, ExternalLink, FileSignature, Lock, Wallet, TrendingUp, Building2,
} from 'lucide-react';
import { ProjectPipelineStepper, useProjectPipeline } from '@/components/projects/ProjectPipelineBadge';
import ClickToCall from '@/components/voice/ClickToCall';
import { ExpenseDialog } from '@/components/expenses/ExpenseDialog';
import ConvertToJobDialog from '@/components/projects/ConvertToJobDialog';
import { Checkbox } from '@/components/ui/checkbox';
import { jobCategoryLabel } from '@/constants/jobCategories';
import { toast } from 'sonner';
import ContactNameStack from '@/components/common/ContactNameStack';
import { isCommercialContact } from '@/lib/contactDisplay';

function EntityCard({ icon: Icon, iconColor, label, items, idKey, numberKey, onViewItem, onCreateNew, subtitleOf, badgeOf }) {
  const count = items.length;
  return (
    <Card className="bg-white border-gray-100 shadow-sm hover:shadow-md transition-shadow" data-testid={`project-${label.toLowerCase()}-card`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <Icon className={`w-5 h-5 ${iconColor}`} />
          <span className="text-2xl font-bold text-gray-900">{count}</span>
        </div>
        <p className="text-xs font-medium text-gray-500">{label}</p>
        {count > 0 && (
          <div className="mt-2 space-y-1.5">
            {items.slice(0, 3).map(item => (
              <div
                key={item.id}
                className="cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5 -mx-1 transition-colors"
                onClick={(e) => { e.stopPropagation(); onViewItem(item.id); }}
                data-testid={`entity-link-${item.id}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs text-blue-600 hover:text-blue-800 truncate font-medium flex items-center gap-1">
                    <ExternalLink className="w-3 h-3 flex-shrink-0" />
                    {item[numberKey] || item.id.slice(0, 8)}
                    {badgeOf && badgeOf(item) && <span className="text-gray-400 font-normal">· {badgeOf(item)}</span>}
                  </span>
                  <StatusBadge status={item.status} />
                </div>
                {subtitleOf && subtitleOf(item) && (
                  <p className="text-[10px] text-gray-400 truncate pl-4" title={subtitleOf(item)}>{subtitleOf(item)}</p>
                )}
              </div>
            ))}
            {count > 3 && <p className="text-[10px] text-gray-400 text-center">+{count - 3} más</p>}
          </div>
        )}
        <div className="flex gap-2 mt-3 pt-2 border-t border-gray-100">
          {count > 0 && (
            <Button
              size="sm"
              variant="outline"
              className="flex-1 h-7 text-xs"
              onClick={(e) => { e.stopPropagation(); onViewItem(items[0].id); }}
              data-testid={`view-${label.toLowerCase()}-btn`}
            >
              <Eye className="w-3 h-3 mr-1" /> Ver{count > 1 ? ` (${count})` : ''}
            </Button>
          )}
          <Button
            size="sm"
            className="flex-1 h-7 text-xs bg-[#003366] hover:bg-[#004080] text-white"
            onClick={(e) => { e.stopPropagation(); onCreateNew(); }}
            data-testid={`new-${label.toLowerCase()}-btn`}
          >
            <Plus className="w-3 h-3 mr-1" /> Nuevo
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Estimates / Invoices card with row selection + direct conversion actions,
 * so Alian never has to leave the project to convert (his video, punto C).
 */
function ConvertibleEntityCard({
  icon: Icon, iconColor, label, items, numberKey, kind,
  selectedId, onSelect, onViewItem, onCreateNew, onConvertToInvoice, onConvertToJob, onOpenJob, onOpenInvoice,
}) {
  const count = items.length;
  const selected = items.find(i => i.id === selectedId) || null;
  const canToInvoice = kind === 'estimate' && selected && selected.status === 'accepted' && !selected.converted_to_invoice_id;
  // all_items_converted is the source of truth going forward (TEMA B2); the
  // converted_to_job_id fallback covers estimates converted before this fix,
  // where that flag may not have been backfilled yet.
  const canToJob = selected && !selected.all_items_converted && !(kind === 'estimate' && selected.converted_to_job_id && !selected.converted_items);

  const addressOf = (item) => item.well_address || item.location?.address || '';

  return (
    <Card className="bg-white border-gray-100 shadow-sm hover:shadow-md transition-shadow" data-testid={`project-${label.toLowerCase()}-card`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <Icon className={`w-5 h-5 ${iconColor}`} />
          <span className="text-2xl font-bold text-gray-900">{count}</span>
        </div>
        <p className="text-xs font-medium text-gray-500">{label}</p>

        {count > 0 && (
          <div className="mt-2 space-y-1 max-h-52 overflow-y-auto pr-0.5">
            {items.map(item => {
              const convertedList = Object.values(item.converted_items || {});
              // Dedupe by job_id (several items can point to the same job)
              const distinctJobs = [];
              const seenJobIds = new Set();
              for (const info of convertedList) {
                if (info?.job_id && !seenJobIds.has(info.job_id)) {
                  seenJobIds.add(info.job_id);
                  distinctJobs.push(info);
                }
              }
              if (distinctJobs.length === 0 && item.converted_to_job_id) {
                // Legacy single-job estimates converted before TEMA B2
                distinctJobs.push({ job_id: item.converted_to_job_id, job_number: item.converted_to_job_number, job_type: item.converted_to_job_type });
              }
              return (
                <div
                  key={item.id}
                  data-testid={`project-${kind}-row-${item.id}`}
                  className={`rounded px-1.5 py-1 -mx-1 border transition-colors ${selectedId === item.id ? 'bg-blue-50/70 border-blue-200' : 'border-transparent hover:bg-gray-50'}`}
                >
                  <div className="flex items-center gap-2">
                    <Checkbox
                      data-testid={`select-${kind}-${item.id}`}
                      checked={selectedId === item.id}
                      onCheckedChange={() => onSelect(selectedId === item.id ? null : item.id)}
                      className="h-3.5 w-3.5"
                    />
                    <button
                      type="button"
                      onClick={() => onViewItem(item.id)}
                      data-testid={`entity-link-${item.id}`}
                      className="text-xs text-blue-600 hover:text-blue-800 truncate font-medium flex items-center gap-1 flex-1 min-w-0 text-left"
                    >
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      {item[numberKey] || item.id.slice(0, 8)}
                    </button>
                    <StatusBadge status={item.status} />
                  </div>
                  {addressOf(item) && (
                    <p className="text-[10px] text-gray-400 truncate pl-6" title={addressOf(item)}>{addressOf(item)}</p>
                  )}
                  <div className="pl-6 flex flex-wrap gap-1">
                    {kind === 'estimate' && item.converted_to_invoice_id && (
                      <button
                        type="button"
                        data-testid={`project-estimate-invoice-link-${item.id}`}
                        onClick={() => onOpenInvoice(item.converted_to_invoice_id)}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                      >
                        <Receipt className="w-2.5 h-2.5" />{item.converted_to_invoice_number || 'Invoice'}
                      </button>
                    )}
                    {distinctJobs.map((info, jIdx) => (
                      <button
                        key={info.job_id}
                        type="button"
                        data-testid={`project-${kind}-job-link-${item.id}-${jIdx}`}
                        onClick={() => onOpenJob(info.job_id)}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100"
                      >
                        <Wrench className="w-2.5 h-2.5" />{info.job_number || 'Job'}
                        {info.job_type ? ` · ${jobCategoryLabel(info.job_type)}` : ''}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex flex-wrap gap-2 mt-3 pt-2 border-t border-gray-100">
          {kind === 'estimate' && (
            <Button
              size="sm"
              variant="outline"
              disabled={!canToInvoice}
              className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"
              onClick={() => onConvertToInvoice(selected)}
              data-testid="project-convert-estimate-to-invoice-btn"
            >
              <Receipt className="w-3 h-3 mr-1" /> To Invoice
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            disabled={!canToJob}
            className="h-7 text-xs border-violet-300 text-violet-700 hover:bg-violet-50 disabled:opacity-40"
            onClick={() => onConvertToJob(selected)}
            data-testid={`project-convert-${kind}-to-job-btn`}
          >
            <Wrench className="w-3 h-3 mr-1" /> To Job
          </Button>
          <Button
            size="sm"
            className="h-7 text-xs bg-[#003366] hover:bg-[#004080] text-white ml-auto"
            onClick={onCreateNew}
            data-testid={`new-${label.toLowerCase()}-btn`}
          >
            <Plus className="w-3 h-3 mr-1" /> Nuevo
          </Button>
        </div>
        {!selected && count > 0 && (
          <p className="text-[10px] text-gray-400 mt-1.5">Selecciona un {kind === 'estimate' ? 'estimado' : 'invoice'} para convertirlo</p>
        )}
      </CardContent>
    </Card>
  );
}

export default function ProjectDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [estimates, setEstimates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [permits, setPermits] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [formInstances, setFormInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);
  const [selectedEstimateId, setSelectedEstimateId] = useState(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [convertSource, setConvertSource] = useState(null);
  const { pipeline } = useProjectPipeline(id);

  const convertEstimateToInvoice = async (est) => {
    if (!est) return;
    try {
      const res = await api.post(`/estimates/${est.id}/convert-to-invoice`);
      toast.success(`Invoice ${res.data?.invoice?.invoice_number || ''} creado`);
      setRefreshTick(v => v + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al convertir');
    }
  };

  // Financials (Expenses + Profit)
  const [profit, setProfit] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);

  const fetchFinancials = useCallback(async () => {
    const [pf, ex] = await Promise.all([
      api.get(`/expenses/profit/${id}`).catch(() => ({ data: null })),
      api.get('/expenses', { params: { project_id: id } }).catch(() => ({ data: [] })),
    ]);
    setProfit(pf.data);
    setExpenses(ex.data || []);
  }, [id]);

  useEffect(() => { fetchFinancials(); }, [fetchFinancials]);

  const deleteExpense = async (expId) => {
    try { await api.delete(`/expenses/${expId}`); fetchFinancials(); } catch { /* noop */ }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const projRes = await api.get(`/projects/${id}`);
        setProject(projRes.data);
        const [estRes, jobsRes, permRes, invRes, contactsRes, formsByProj] = await Promise.all([
          api.get('/estimates', { params: { page_size: 500 } }).catch(() => ({ data: { items: [] } })),
          api.get('/jobs', { params: { page_size: 500 } }).catch(() => ({ data: { items: [] } })),
          api.get('/permits').catch(() => ({ data: [] })),
          api.get('/invoices', { params: { page_size: 500 } }).catch(() => ({ data: { items: [] } })),
          projRes.data.customer_id ? api.get(`/contacts/${projRes.data.customer_id}`).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
          api.get('/custom-forms/instances', { params: { linked_type: 'project', linked_id: id } }).catch(() => ({ data: [] })),
        ]);
        const projectJobs = (jobsRes.data.items || jobsRes.data).filter(j => j.project_id === id);
        setEstimates((estRes.data.items || estRes.data).filter(e => e.project_id === id));
        setJobs(projectJobs);
        setPermits(permRes.data.filter(p => p.project_id === id));
        setInvoices((invRes.data.items || invRes.data).filter(i => i.project_id === id));
        setCustomer(contactsRes.data);

        // Fetch form instances linked to this project's jobs as well, so the
        // project view shows EVERY form (Permit Application, NOC, Credit Card,
        // Completion Report, etc.) regardless of whether it was attached at
        // project- or job-level.
        const jobFormReqs = projectJobs.map(j =>
          api.get('/custom-forms/instances', { params: { linked_type: 'job', linked_id: j.id } })
            .then(r => r.data || [])
            .catch(() => [])
        );
        const jobForms = (await Promise.all(jobFormReqs)).flat();
        setFormInstances([...(formsByProj.data || []), ...jobForms]);
      } catch {
        navigate('/projects');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate, refreshTick]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#003366]/20 border-t-[#003366] rounded-full animate-spin" />
      </div>
    );
  }

  if (!project) return null;

  const TYPE_LABELS = {
    new_well: t('projects.types.new_well'),
    new_construction: 'New Construction',
    repair: t('projects.types.repair'),
    modification: t('projects.types.modification'),
    abandonment: t('projects.types.abandonment'),
    pump_service: t('projects.types.pump_service'),
    other: t('projects.types.other')
  };

  const loc = project.location || {};
  const hasLocation = loc.address || loc.city || loc.county;
  const hasCoords = loc.latitude && loc.longitude;
  const parcelId = loc.parcel_id || project.parcel_number;
  // Punto G (Alian): "el nombre del dueno de la propiedad es lo mas
  // importante que debe salir" — Property Owner (dueno legal de la parcela)
  // es distinto del Customer (quien pide/paga) y debe verse destacado.
  const propertyOwner = estimates.find(e => e.property_info?.owner_name)?.property_info?.owner_name || '';

  return (
    <div data-testid="project-detail-page" className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/projects')} className="mt-0.5 h-9 w-9" data-testid="back-to-projects">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-mono text-gray-400 bg-gray-100 px-2 py-0.5 rounded">{project.project_number}</span>
                {project.office_number && (
                  <span className="text-xs font-mono text-gray-400 bg-gray-100 px-2 py-0.5 rounded" data-testid="project-office-number-badge">Office #: {project.office_number}</span>
                )}
                <StatusBadge status={project.status} />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 font-['Barlow'] tracking-tight">{project.title}</h1>
            </div>
            <Button size="sm" onClick={() => navigate('/projects')} className="bg-[#003366] hover:bg-[#004080] text-white self-start">
              <Pencil className="w-4 h-4 mr-2" /> {t('common.edit')}
            </Button>
          </div>
        </div>
      </div>

      {/* Project Pipeline Status — visual workflow shortcut (Alian's video) */}
      {pipeline && (
        <Card className="bg-white border-gray-100 shadow-sm relative overflow-hidden" data-testid="project-pipeline-card">
          <PaymentRibbon label={computePaymentRibbon(invoices)} size="lg" />
          <CardHeader className="pb-2 pt-3">
            <CardTitle className="text-xs uppercase tracking-wider text-gray-500 font-semibold">{t('projects.pipelineTitle', { defaultValue: 'Project Pipeline' })}</CardTitle>
          </CardHeader>
          <CardContent className="pb-4">
            <ProjectPipelineStepper currentStage={pipeline.stage} history={pipeline.history || []} />
          </CardContent>
        </Card>
      )}

      {/* Job Site Location - PROMINENT */}
      {hasLocation && (
        <Card className="bg-white border-blue-100 shadow-sm border-l-4 border-l-[#3399FF]" data-testid="project-location-card">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-1">{t('projects.jobSite')}</p>
                <p className="text-base font-semibold text-gray-900">
                  {loc.address}{loc.city ? `, ${loc.city}` : ''}{loc.state ? `, ${loc.state}` : ''}
                  {loc.zip_code || loc.zip ? ` ${loc.zip_code || loc.zip}` : ''}
                </p>
                {propertyOwner && (
                  <div className="mt-2 inline-flex flex-col bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5" data-testid="project-owner-prominent">
                    <span className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">{t('permits.propertyOwner')}</span>
                    <span className="text-sm font-bold text-gray-900 leading-tight">{propertyOwner}</span>
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
                  {loc.county && (
                    <span className="inline-flex items-center gap-1 text-sm text-blue-700 font-medium">
                      <Landmark className="w-3.5 h-3.5" /> {loc.county} County
                    </span>
                  )}
                  {hasCoords && (
                    <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                      <Navigation className="w-3 h-3" /> {parseFloat(loc.latitude).toFixed(5)}, {parseFloat(loc.longitude).toFixed(5)}
                    </span>
                  )}
                  {parcelId && (
                    <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                      Parcel: <span className="font-mono">{parcelId}</span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Project Info + Customer */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 bg-white border-gray-100 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold font-['Barlow'] flex items-center gap-2">
              <FolderKanban className="w-4 h-4 text-[#3399FF]" /> {t('projects.projectInfo')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{t('projects.type')}</p>
                <p className="text-sm font-medium text-gray-900 mt-0.5">{TYPE_LABELS[project.type] || project.type}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{t('bookings.tableHeaders.status')}</p>
                <div className="mt-0.5"><StatusBadge status={project.status} /></div>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{t('common.created')}</p>
                <p className="text-sm text-gray-700 mt-0.5">{project.created_at?.slice(0, 10)}</p>
              </div>
            </div>
            {project.description && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{t('jobDetail.description')}</p>
                <p className="text-sm text-gray-700 mt-0.5">{project.description}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-100 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold font-['Barlow'] flex items-center gap-2">
              <User className="w-4 h-4 text-[#3399FF]" /> {t('jobDetail.customerTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {customer ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#003366] to-[#3399FF] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {isCommercialContact(customer) ? <Building2 className="w-4 h-4" /> : `${customer.first_name?.[0] || ''}${customer.last_name?.[0] || ''}`}
                  </div>
                  <ContactNameStack contact={customer} primaryClassName="text-sm font-semibold text-gray-900" secondaryClassName="text-xs text-gray-500" testId="project-detail-customer-display" />
                </div>
                {customer.phone && (
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-gray-600">{customer.phone}</p>
                    <ClickToCall number={customer.phone} name={`${customer.first_name || ''} ${customer.last_name || ''}`.trim()} />
                  </div>
                )}
                {customer.email && <p className="text-sm text-gray-600">{customer.email}</p>}
              </div>
            ) : (
              <p className="text-sm text-gray-400">{t('contacts.noContactSelected')}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Related entities - Order: Estimates → Invoices → Permits → Jobs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <ConvertibleEntityCard
          icon={FileText}
          iconColor="text-blue-500"
          label="Estimates"
          items={estimates}
          numberKey="estimate_number"
          kind="estimate"
          selectedId={selectedEstimateId}
          onSelect={setSelectedEstimateId}
          onViewItem={(itemId) => navigate('/estimates', { state: { openEstimateId: itemId } })}
          onCreateNew={() => navigate('/estimates', { state: { createForProject: id, createFor: project.customer_id } })}
          onConvertToInvoice={convertEstimateToInvoice}
          onConvertToJob={(item) => setConvertSource({ kind: 'estimate', id: item.id, number: item.estimate_number, subtitle: item.well_address })}
          onOpenJob={(jobId) => navigate(`/jobs/${jobId}`)}
          onOpenInvoice={(invId) => navigate('/invoices', { state: { openInvoiceId: invId } })}
        />
        <ConvertibleEntityCard
          icon={Receipt}
          iconColor="text-emerald-500"
          label="Invoices"
          items={invoices}
          numberKey="invoice_number"
          kind="invoice"
          selectedId={selectedInvoiceId}
          onSelect={setSelectedInvoiceId}
          onViewItem={(itemId) => navigate('/invoices', { state: { openInvoiceId: itemId } })}
          onCreateNew={() => navigate('/invoices', { state: { createForProject: id, createFor: project.customer_id } })}
          onConvertToJob={(item) => setConvertSource({ kind: 'invoice', id: item.id, number: item.invoice_number, subtitle: item.title })}
          onOpenJob={(jobId) => navigate(`/jobs/${jobId}`)}
          onOpenInvoice={(invId) => navigate('/invoices', { state: { openInvoiceId: invId } })}
        />
        <EntityCard
          icon={Shield}
          iconColor="text-amber-500"
          label="Permits"
          items={permits}
          numberKey="permit_number"
          onViewItem={(itemId) => navigate(`/permits?open=${itemId}`)}
          onCreateNew={() => navigate(`/permits?action=create&project_id=${id}`)}
        />
        <EntityCard
          icon={Wrench}
          iconColor="text-[#3399FF]"
          label="Jobs"
          items={jobs}
          numberKey="job_number"
          badgeOf={(item) => jobCategoryLabel(item.type)}
          subtitleOf={(item) => item.well_address || item.location?.address || ''}
          onViewItem={(itemId) => navigate('/jobs', { state: { editJobId: itemId } })}
          onCreateNew={() => navigate('/jobs', { state: { createForProject: id, createFor: project.customer_id } })}
        />
      </div>

      {/* Forms attached to this project (and its jobs) */}
      {formInstances.length > 0 && (
        <Card className="bg-white border-gray-100 shadow-sm" data-testid="project-forms-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold font-['Barlow'] flex items-center gap-2">
              <FileSignature className="w-4 h-4 text-violet-500" />
              {t('projects.formsTitle', { defaultValue: 'Forms & Permits' })}
              <span className="text-xs font-normal text-gray-400">({formInstances.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {formInstances.map(inst => {
              const isSigned = !!inst.signed_at || (inst.status || '').toLowerCase() === 'signed';
              const date = (inst.signed_at || inst.filled_at || inst.created_at || '').slice(0, 10);
              const openInstance = async () => {
                if (isSigned) {
                  try {
                    const res = await api.get(`/custom-forms/instances/${inst.id}/pdf`, { responseType: 'blob' });
                    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                    window.open(url, '_blank', 'noopener');
                  } catch { /* noop */ }
                  return;
                }
                navigate('/permits-registry');
              };
              return (
                <div
                  key={inst.id}
                  data-testid={`project-form-${inst.id}`}
                  onClick={openInstance}
                  className="flex items-center justify-between gap-2 py-2 px-2 rounded hover:bg-gray-50 cursor-pointer"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-violet-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{inst.template_name || 'Form'}</p>
                      <p className="text-xs text-gray-400">{date || '—'}</p>
                    </div>
                  </div>
                  {isSigned ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase bg-emerald-50 text-emerald-700">
                      <Lock className="w-2.5 h-2.5" /> Signed
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase bg-amber-50 text-amber-700">
                      <Pencil className="w-2.5 h-2.5" /> Pending
                    </span>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Financials: Revenue − Expenses = Profit (per-item breakdown) */}
      <Card className="bg-white border-gray-100 shadow-sm" data-testid="project-financials-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-sm font-semibold font-['Barlow'] flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-500" /> {t('expenses.financials')}
            </CardTitle>
            <Button data-testid="add-project-expense-btn" size="sm" variant="outline" onClick={() => { setEditingExpense(null); setExpenseDialogOpen(true); }}>
              <Plus className="w-3.5 h-3.5 mr-1" /> {t('expenses.addExpense')}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Summary tiles */}
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg bg-blue-50 border border-blue-100 p-3 text-center">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">{t('expenses.revenue')}</p>
              <p className="text-lg font-bold text-blue-700 mt-0.5" data-testid="financials-revenue">${(profit?.revenue || 0).toFixed(2)}</p>
            </div>
            <div className="rounded-lg bg-rose-50 border border-rose-100 p-3 text-center">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">{t('expenses.expenses')}</p>
              <p className="text-lg font-bold text-rose-700 mt-0.5" data-testid="financials-expenses">${(profit?.total_expenses || 0).toFixed(2)}</p>
            </div>
            <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3 text-center">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">{t('expenses.profit')}</p>
              <p className={`text-lg font-bold mt-0.5 ${(profit?.profit || 0) >= 0 ? 'text-emerald-700' : 'text-red-600'}`} data-testid="financials-profit">${(profit?.profit || 0).toFixed(2)}</p>
              {profit?.revenue > 0 && <p className="text-[10px] text-gray-400">{profit.margin_pct}% {t('expenses.margin')}</p>}
            </div>
          </div>

          {/* Per-item breakdown */}
          {profit?.items?.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-gray-100">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-100">
                    <th className="text-left text-[10px] uppercase tracking-wider text-gray-500 font-semibold px-3 py-2">{t('expenses.item')}</th>
                    <th className="text-right text-[10px] uppercase tracking-wider text-gray-500 font-semibold px-3 py-2">{t('expenses.charged')}</th>
                    <th className="text-right text-[10px] uppercase tracking-wider text-gray-500 font-semibold px-3 py-2">{t('expenses.expense')}</th>
                    <th className="text-right text-[10px] uppercase tracking-wider text-gray-500 font-semibold px-3 py-2">{t('expenses.profit')}</th>
                  </tr>
                </thead>
                <tbody>
                  {profit.items.map((it, i) => (
                    <tr key={i} data-testid={`profit-item-${i}`} className="border-b border-gray-50 last:border-0">
                      <td className="px-3 py-2 text-gray-700">{it.description} <span className="text-gray-300 text-xs">· {it.estimate_number}</span></td>
                      <td className="px-3 py-2 text-right text-gray-600">${it.charged.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right text-rose-600">{it.expense > 0 ? `-$${it.expense.toFixed(2)}` : '—'}</td>
                      <td className={`px-3 py-2 text-right font-semibold ${it.profit >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>${it.profit.toFixed(2)}</td>
                    </tr>
                  ))}
                  {profit.unassigned_expenses > 0 && (
                    <tr className="border-t border-gray-100 bg-rose-50/40">
                      <td className="px-3 py-2 text-gray-500 italic">{t('expenses.unassigned')}</td>
                      <td className="px-3 py-2 text-right text-gray-400">—</td>
                      <td className="px-3 py-2 text-right text-rose-600">-${profit.unassigned_expenses.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right text-red-600 font-semibold">-${profit.unassigned_expenses.toFixed(2)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Expense list */}
          {expenses.length > 0 ? (
            <div className="space-y-1">
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{t('expenses.title')} ({expenses.length})</p>
              {expenses.map(e => (
                <div key={e.id} data-testid={`project-expense-${e.id}`} className="flex items-center justify-between gap-2 py-2 px-2 rounded hover:bg-gray-50">
                  <div className="min-w-0">
                    <p className="text-sm text-gray-800 truncate">
                      <span className="capitalize font-medium">{t(`expenses.types.${e.expense_type}`, { defaultValue: e.expense_type })}</span>
                      {e.description ? ` · ${e.description}` : ''}
                    </p>
                    <p className="text-xs text-gray-400">
                      {e.beneficiary_name || t('expenses.noBeneficiary')}
                      {e.item_index !== null && e.item_index !== undefined ? ` · ${e.item_description || `item #${e.item_index + 1}`}` : ''}
                      {e.date ? ` · ${e.date.slice(0, 10)}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded uppercase ${e.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`} data-testid={`project-expense-status-${e.id}`}>
                      {e.payment_status === 'paid' ? t('expenses.paid') : t('expenses.unpaid')}
                    </span>
                    <span className={`text-sm font-semibold ${e.payment_status === 'paid' ? 'text-emerald-700' : 'text-amber-700'}`}>${(e.amount || 0).toFixed(2)}</span>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditingExpense(e); setExpenseDialogOpen(true); }}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={() => deleteExpense(e.id)}><Pencil className="w-3.5 h-3.5 hidden" /><span className="text-xs">✕</span></Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-2">{t('expenses.noExpensesProject')}</p>
          )}
        </CardContent>
      </Card>

      {/* Documents Section */}
      <DocumentsSection entityType="project" entityId={id} />

      {/* Activity Log (read-only) */}
      <ActivityLog entityType="projects" entityId={id} />

      <ExpenseDialog
        open={expenseDialogOpen}
        onClose={() => setExpenseDialogOpen(false)}
        onSaved={fetchFinancials}
        editing={editingExpense}
        defaultProjectId={id}
      />

      <ConvertToJobDialog
        open={!!convertSource}
        source={convertSource}
        onClose={() => setConvertSource(null)}
        onDone={() => setRefreshTick(v => v + 1)}
      />
    </div>
  );
}
